import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { BillingCancellationOptionType, BillingCancellationReason } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent, { type Args } from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent, { IconPosition } from 'webapp/components/submit-button';

interface RetentionOffer {
	icon: IconName;
	title: string;
	description: string;
	lead: string;
	bullets: string[];
	meetingText: string;
}

const SPECIALIST_LEAD = 'Meet with a Databox specialist to:';
const SPECIALIST_SEE_HOW_LEAD = 'Meet with a Databox specialist to see how to:';
const ACCOUNT_REVIEW_TEXT = 'Book a free account review';

const OFFERS: Record<BillingCancellationReason, RetentionOffer> = {
	[BillingCancellationReason.AI_TOOLS]: {
		icon: 'Ai',
		title: 'Get more from AI with your existing setup',
		description:
			"Before moving your reporting and analysis to other AI tools, see how Databox's AI Analyst can help you get more relevant answers and automate recurring work, using the data and context already in place.",
		lead: SPECIALIST_LEAD,
		bullets: [
			'See how AI can analyze performance using your metrics and goals',
			'Learn how to automate recurring analysis and reports',
			'Explore ways to eliminate manual exports and repetitive prompts',
		],
		meetingText: ACCOUNT_REVIEW_TEXT,
	},
	[BillingCancellationReason.OWN_AI_TOOL]: {
		icon: 'Ai',
		title: 'Get the benefit of AI reporting without the upkeep',
		description:
			"Before switching to your own AI solution, see how Databox's latest AI capabilities could help you achieve the same goals with less effort.",
		lead: SPECIALIST_SEE_HOW_LEAD,
		bullets: [
			'Use your existing data connections and metrics with AI',
			'Automate analysis and reporting with ready-to-use AI capabilities',
			'Reduce the ongoing work of maintaining a custom solution',
		],
		meetingText: ACCOUNT_REVIEW_TEXT,
	},
	[BillingCancellationReason.BI_COMPETITOR]: {
		icon: 'Table',
		title: 'Advanced analytics without the added complexity',
		description:
			"Before exploring other BI platforms, see how Databox's latest analytics and AI capabilities could help you work with more complex data and get the insights you need.",
		lead: SPECIALIST_SEE_HOW_LEAD,
		bullets: [
			'Connect SQL databases and cloud applications',
			'Combine and model data with datasets',
			'Build custom reports and analyze performance with AI',
		],
		meetingText: ACCOUNT_REVIEW_TEXT,
	},
	[BillingCancellationReason.BUILT_IN_ANALYTICS]: {
		icon: 'ChartCombo',
		title: 'See what you might miss with built-in reporting',
		description:
			"Built-in reporting shows you what's happening in each tool, but it doesn't always tell the full story. Databox brings your data together so you can see how your business is performing and uncover deeper insights with AI.",
		lead: SPECIALIST_SEE_HOW_LEAD,
		bullets: [
			'Connect data from your business tools',
			'Track KPIs across marketing, sales, and revenue, in one place',
			'Use AI to analyze performance and automate reporting',
		],
		meetingText: ACCOUNT_REVIEW_TEXT,
	},
	[BillingCancellationReason.UNRESOLVED_ISSUES]: {
		icon: 'Chat',
		title: "We'd like a chance to make things right",
		description:
			"We're sorry we couldn't resolve your issues. We'd like to personally review what happened and see what we can do to help.",
		lead: SPECIALIST_LEAD,
		bullets: [
			'Review your previous issues and support history',
			"Investigate what's still unresolved",
			'Explore possible solutions or alternatives',
		],
		meetingText: 'Book a call with a specialist',
	},
	[BillingCancellationReason.STOPPED_REPORTING]: {
		icon: 'ListCheck',
		title: 'Your reporting needs changed. Your plan can too.',
		description:
			'Even if reporting is no longer a priority, you may still want to keep an eye on a few key metrics.',
		lead: 'Meet one-on-one with a Databox specialist to:',
		bullets: [
			'Identify which key metrics are still worth tracking',
			'Explore how AI can keep you informed automatically',
			'Find a plan that better fits your needs and budget',
		],
		meetingText: ACCOUNT_REVIEW_TEXT,
	},
};

export default class AccountBillingCancellationFlowPopupRetentionOfferComponent extends PopupInnerModalComponent<PopupData.Billing.CancellationRetentionOffer> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.CANCELLATION_OFFER_SHOWN, {
			choices: [BillingCancellationOptionType.SUPPORT, BillingCancellationOptionType.CANCEL],
		});
	}

	get offer(): RetentionOffer {
		return OFFERS[this.popupData.reason];
	}

	triggerOfferCompletedEvent(choice: BillingCancellationOptionType): void {
		this.analyticsService.event(TrackingEvent.CANCELLATION_OFFER_COMPLETED, {
			choice,
			feedback_choice: this.flowCancellationService.getFeedback()?.choice,
		});
	}

	@action
	bookMeeting(): void {
		this.triggerOfferCompletedEvent(BillingCancellationOptionType.SUPPORT);
		this.flowCancellationService.openCancellationMeeting('cancellation_offer');
		this.close();
	}

	@action
	skip(): void {
		this.triggerOfferCompletedEvent(BillingCancellationOptionType.CANCEL);
		this.flowCancellationService.openConfirmationPopup();
	}
	<template>
		<div class="content">
			<CommonUiIconComponent @name={{this.offer.icon}} @size="2xl" />
			<h2>{{this.offer.title}}</h2>
			<p>{{this.offer.description}}</p>

			<div class="offer-benefits">
				<strong>{{this.offer.lead}}</strong>
				{{#each this.offer.bullets as |bullet|}}
					<div class="offer-benefits__item">
						<CommonUiIconComponent @name="Check" @size="md" />
						{{bullet}}
					</div>
				{{/each}}
			</div>

			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text={{this.offer.meetingText}}
					@size="big"
					@icon="External"
					@iconPosition={{IconPosition.AFTER}}
					@noDelay={{true}}
					@onTrigger={{this.bookMeeting}}
				/>
				<SubmitButtonComponent
					@text="Continue cancellation"
					@theme="grey-empty"
					@onTrigger={{this.skip}}
				/>
			</div>
		</div>
	</template>
}
